import {useMemo} from 'react';
import {useQuery} from 'react-query';
import {GoalsRequestImpl} from '@api/goals-api/goals-request';
import {useViewInsight} from '@hooks/insights';
import {Body2} from '@ui/atoms/typography';
import {Button} from '@ui/atoms/button';
import {ArrowRightIcon} from '@ui/atoms/icons';
import {ItemLoader} from '@ui/organisms/item-loader';
import {getCheckinGoalProgress} from '../goal-reflection-snapshot';

export const goalsFromCheckinResponse = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.goals)) return response.goals;
  return [];
};

const goalTypeLabel = (goal?: any) => {
  const type = String(goal?.goalType || '').toLowerCase();
  if (type === 'company' || type === 'company-wide') return 'Company-wide';
  if (type === 'group' || type === 'team') return 'Team';
  return 'Personal';
};

export const useCheckinGoals = (userId?: string, enabled = true) => {
  const request = useMemo(() => new GoalsRequestImpl(), []);
  const {data, isLoading, isError, refetch} = useQuery(
    ['checkin-goals', userId],
    async () => {
      const response = await request.fetchGoals(
        1,
        'activeCheckins',
        100,
        undefined,
        null,
        userId,
      );
      if (!response) throw new Error('Unable to load goals');
      return response;
    },
    {
      enabled: Boolean(userId) && enabled,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const goals = useMemo(
    () => goalsFromCheckinResponse(data).filter((goal) => !goal?.isKpi),
    [data],
  );

  return {goals, isLoading, isError, refetch};
};

export type CheckinGoalsState = ReturnType<typeof useCheckinGoals>;

export const CheckinGoalsList = ({
  userId,
  state,
}: {
  userId?: string;
  state: CheckinGoalsState;
}) => {
  const viewGoalInsight = useViewInsight();
  const {goals, isLoading, isError, refetch} = state;

  if (!userId) {
    return (
      <div className="rounded-[10px] border border-borderLight bg-backgroundLight p-4 text-center">
        <Body2 kind="textBody">Goals are unavailable for this response.</Body2>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center rounded-[10px] border border-borderLight bg-backgroundLight py-5">
        <ItemLoader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-between gap-4 rounded-[10px] border border-borderLight bg-backgroundLight p-4">
        <Body2 kind="textBody">Unable to load goals.</Body2>
        <Button kind="secondary" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!goals.length) {
    return (
      <div className="rounded-[10px] border border-borderLight bg-backgroundLight p-4 text-center">
        <Body2 kind="textBody">No active goals to show.</Body2>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" aria-label="Active goals">
      {goals.map((goal) => {
        const progress = getCheckinGoalProgress(goal);
        return (
          <button
            key={goal.id}
            type="button"
            onClick={() => viewGoalInsight(goal.id, false)}
            aria-label={`Open ${goal.name || 'goal'} activity`}
            className="flex w-full items-center gap-3 rounded-[10px] border border-borderLight bg-white p-3 text-left transition-colors hover:border-purple200 hover:bg-backgroundLight focus:outline-none focus-visible:ring-2 focus-visible:ring-purple300">
            <div className="min-w-0 flex-1">
              <Body2 kind="blackTwo" weight="semibold" className="truncate">
                {goal.name || 'Untitled goal'}
              </Body2>
              <Body2 kind="textBody" size="12px">
                {goalTypeLabel(goal)}
              </Body2>
            </div>
            <div className="flex min-w-[104px] items-center gap-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-borderLight">
                <div
                  className="h-full rounded-full bg-green300"
                  style={{width: `${progress}%`}}
                />
              </div>
              <Body2 kind="blackTwo" size="12px">
                {Math.round(progress)}%
              </Body2>
            </div>
            <span className="shrink-0 text-purple300" aria-hidden="true">
              <ArrowRightIcon style={{stroke: 'currentColor'}} />
            </span>
          </button>
        );
      })}
      <Body2 kind="textBody" size="12px" className="mt-1">
        Select a goal to view its activity and update history.
      </Body2>
    </div>
  );
};

export const CheckinGoals = ({userId}: {userId?: string}) => {
  const state = useCheckinGoals(userId);
  return <CheckinGoalsList userId={userId} state={state} />;
};
